import {
	ReactElement,
	useEffect,
	useMemo,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { SettingsPage } from "../../SettingsPage";
import {
	BillingEnvironmentType,
	BillingViewModel,
	CustomerInfoModel,
	CustomerInfoSaveModel,
	Industry,
	load,
	saveCustomerInfo,
	SubIndustry
} from "client/Api/Licensing/Billing/billing";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import {
	BillingFormData,
	CustomerSectionForm,
	getSubIndustriesOfIndustry,
	IndustryWithSubIndustries,
	isIndustryWithSubIndustries
} from "./CustomerSection";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { UserLicenseSection } from "./UserLicenseSection";
import { ToolbarAction } from "client/Components";
import {
	EMPTY_ARRAY,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const BillingPage = (): ReactElement => {
	const [customerFormData, setCustomerFormData] = useState<BillingFormData>(EMPTY_BILLING_FORM_DATA);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const formHandler: IFormHandler<BillingFormData> = useFormHandler(
		customerFormData,
		setCustomerFormData,
		validationConfiguration
	);
	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	useActiveSettingsMenuItem(SettingsPageKind.BillingInfo);

	const defaultActions: readonly BillingAction[] = useMemo(
		() => {
			const saveHandler = async (): Promise<void> => {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate();

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					return;
				}

				try {
					setIsSaving(true);
					const saveModel: CustomerInfoSaveModel = convertToCustomerInfoSaveModel(
						customerFormData
					);

					await saveCustomerInfo(saveModel);

					setCustomerFormData(
						convertToBillingFormData(saveModel)
					);

					alertManager.showSuccess(localize("_ChangesSaved"));
				} catch (e) {
					if (!formHandler.validation.tryProcessServerError(e)) {
						alertManager.showError(localize("_GeneralErrorMessage"));
					}
				} finally {
					setIsSaving(false);
				}
			};

			return listDefaultActions({
				doneHandler() {
					void saveHandler();
				}
			});
		},
		[
			alertManager,
			customerFormData,
			formHandler.validation
		]
	);

	const topBarActions = useMemo(
		(): readonly BillingAction[] => {
			return buildActionsForTopBar<undefined, BillingAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	useEffect(
		() => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsLoading(true);

				try {
					const model: BillingViewModel = await load(signal)
						.catch(handlePageDataLoadingError);

					setCustomerFormData(
						convertToBillingFormData(model.customerInfo)
					);

					setIsLoading(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsLoading(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isLoading || isSaving}
			actions={topBarActions}
			actionContext={undefined}
			title={localize("_CompanyInformation")}
		>
			<SettingsPage.Section>
				<CustomerSectionForm
					attachable={formHandler}
					hasWidthConstrained
					canSelectEnvironmentType
				/>
			</SettingsPage.Section>

			<UserLicenseSection />
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};

BillingPage.displayName = "BillingOverview";

type BillingActionContext = undefined;

type BillingAction = ToolbarAction<BillingActionContext>;

export const validationConfiguration: IValidationConfiguration<BillingFormData> =
	createValidationConfiguration<BillingFormData>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(250)
	);

validationConfiguration.forScalar("customerType")
	.add(
		isNotNullNorUndefined()
	);

validationConfiguration.forScalar("industry")
	.add(
		isNotNullNorUndefined()
	);

validationConfiguration.when(
	x => isNotNil(x.industry) && isIndustryWithSubIndustries(x.industry)
)
	.forObject("subIndustries")
	.add(
		inline(
			(value: readonly SubIndustry[], validationContext): InputValidationResult => {
				const parent = validationContext.parentValue as BillingFormData;

				if (isNil(parent.industry) || !isIndustryWithSubIndustries(parent.industry)) {
					return {
						kind: InputValidationResultKind.Valid
					};
				}

				if (getSubIndustriesOfIndustry(parent.industry, value).length !== 0) {
					return {
						kind: InputValidationResultKind.Valid
					};
				}

				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_IsRequired")
				};
			}
		)
	);

validationConfiguration.when(
	x => x.industry === Industry.Other
)
	.forScalar("otherIndustry")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(50)
	);

validationConfiguration.forScalar("employeeSize")
	.add(
		isNotNullNorUndefined()
	);

validationConfiguration.forScalar("referenceNumber")
	.add(
		doesNotExceedLength(50)
	);

export const EMPTY_BILLING_FORM_DATA: BillingFormData = {
	name: "",
	referenceNumber: "",
	customerType: null,
	employeeSize: null,
	environmentType: BillingEnvironmentType.Default,
	industry: null,
	otherIndustry: null,
	subIndustries: EMPTY_ARRAY,
	industriesWithOtherSelected: new Set<IndustryWithSubIndustries>()
};

export function convertToBillingFormData(
	model: CustomerInfoModel | CustomerInfoSaveModel
): BillingFormData {
	const {
		name,
		referenceNumber,
		customerType,
		industry,
		employeeSize,
		environmentType,
		otherIndustry,
		subIndustries
	} = model;

	const industriesWithOtherSelected = new Set<IndustryWithSubIndustries>();

	if (isNotNil(industry)
		&& isIndustryWithSubIndustries(industry)
		&& subIndustries?.includes(SubIndustry.Other)
	) {
		industriesWithOtherSelected.add(industry);
	}

	return {
		name: name ?? "",
		referenceNumber,
		customerType,
		employeeSize,
		environmentType: environmentType ?? BillingEnvironmentType.Default,
		industry,
		otherIndustry: industry === Industry.Other ? otherIndustry : null,
		subIndustries: subIndustries ?? EMPTY_ARRAY,
		industriesWithOtherSelected
	};
}

export function convertToCustomerInfoSaveModel(
	model: BillingFormData
): CustomerInfoSaveModel {
	const {
		name,
		referenceNumber,
		customerType,
		employeeSize,
		environmentType,
		industry
	} = model;

	return {
		name,
		referenceNumber,
		customerType,
		employeeSize,
		environmentType,
		industry,
		otherIndustry: model.industry === Industry.Other
			? model.otherIndustry
			: null,
		subIndustries: isNotNil(model.industry) && isIndustryWithSubIndustries(model.industry)
			? getSubIndustriesOfIndustry(model.industry, model.subIndustries)
			: null
	};
}

type DefaultActionsHandlers = {
	doneHandler(): void;
};

function listDefaultActions(
	handlers: DefaultActionsHandlers
): readonly BillingAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.doneHandler(),
			icon: "floppy-disk",
			isPrimary: true
		}
	];
}

export const billingRouting = {
	page: "/Billing"
};